import { useEffect, useMemo, useState } from 'react'
import { getApiErrorMessage } from '../api'
import { previewCustomQuote } from '../api/graph_settings'
import '../styles/pages/settings.css'

export const CUSTOM_CAPABILITY_SETTING_KEYS = [
  'custom_requests_enabled',
  'custom_capabilities_configured',
  'custom_photo_enabled',
  'custom_video_enabled',
  'custom_audio_enabled',
  'personalized_audio_fantasy_enabled',
  'personalized_audio_fantasy_min_words',
  'personalized_audio_fantasy_soft_offer_after_signals',
  'custom_name_personalization_enabled',
  'custom_face_allowed',
  'custom_external_items_allowed',
  'custom_outfit_purchase_allowed',
  'custom_prop_purchase_allowed',
  'custom_max_purchase_budget',
  'custom_max_nudity_level',
  'custom_toy_use_allowed',
  'custom_toy_insertion_allowed',
  'custom_other_object_insertion_allowed',
  'custom_gg_content_allowed',
  'custom_bg_content_allowed',
  'custom_max_photo_count',
  'custom_max_video_minutes',
  'custom_max_audio_minutes',
  'custom_standard_turnaround_days',
  'custom_allowed_scenarios',
  'custom_forbidden_scenarios',
  'custom_available_outfits',
  'custom_available_locations',
  'custom_available_prop_categories',
  'custom_available_props',
]

export const CUSTOM_CAPABILITY_GROUPS = [
  {
    id: 'availability',
    title: 'Availability and delivery',
    keys: ['custom_requests_enabled', 'custom_capabilities_configured', 'custom_photo_enabled', 'custom_video_enabled', 'custom_audio_enabled', 'personalized_audio_fantasy_enabled', 'personalized_audio_fantasy_min_words', 'personalized_audio_fantasy_soft_offer_after_signals', 'custom_name_personalization_enabled', 'custom_face_allowed', 'custom_max_photo_count', 'custom_max_video_minutes', 'custom_max_audio_minutes', 'custom_standard_turnaround_days'],
  },
  {
    id: 'boundaries',
    title: 'Nudity and explicit boundaries',
    keys: ['custom_max_nudity_level', 'custom_toy_use_allowed', 'custom_toy_insertion_allowed', 'custom_other_object_insertion_allowed'],
  },
  {
    id: 'partners',
    title: 'Partner content',
    keys: ['custom_gg_content_allowed', 'custom_bg_content_allowed'],
  },
  {
    id: 'production',
    title: 'Wardrobe, purchases and props',
    keys: ['custom_outfit_purchase_allowed', 'custom_prop_purchase_allowed', 'custom_external_items_allowed', 'custom_max_purchase_budget', 'custom_available_prop_categories', 'custom_available_outfits', 'custom_available_locations', 'custom_available_props'],
  },
  {
    id: 'scenarios',
    title: 'Scenario boundaries',
    keys: ['custom_allowed_scenarios', 'custom_forbidden_scenarios'],
  },
]

export const CUSTOM_NUDITY_OPTIONS = [
  ['clothed', 'Clothed / non-nude'],
  ['lingerie', 'Lingerie'],
  ['topless', 'Topless'],
  ['full_nude', 'Full nude'],
  ['explicit_closeup', 'Explicit close-up'],
]

export const CUSTOM_PROP_OPTIONS = [
  ['lingerie', 'Lingerie'],
  ['stockings', 'Stockings'],
  ['high_heels', 'High heels'],
  ['costume', 'Costume / cosplay'],
  ['mask', 'Mask'],
  ['blindfold', 'Blindfold'],
  ['restraints', 'Restraints'],
  ['vibrator', 'Vibrator'],
  ['dildo', 'Dildo'],
  ['butt_plug', 'Butt plug'],
  ['body_oil', 'Body oil'],
  ['candles', 'Candles'],
]

export const CUSTOM_PRICE_SETTING_KEYS = [
  'custom_pricing_enabled',
  'custom_pricing_currency',
  'custom_pricing_base_fee',
  'custom_pricing_minimum_total',
  'custom_pricing_video_per_minute',
  'custom_pricing_audio_per_minute',
  'custom_pricing_photo_each',
  'custom_pricing_name_markup_percent',
  'custom_pricing_urgency_72h_percent',
  'custom_pricing_urgency_24h_percent',
  'custom_pricing_negotiation_margin_percent',
  'custom_pricing_minimum_deposit_percent',
]

export const CUSTOM_PRICING_SETTING_KEYS = [
  ...CUSTOM_CAPABILITY_SETTING_KEYS,
  ...CUSTOM_PRICE_SETTING_KEYS,
]

export const CUSTOM_CAPABILITY_BOOLEAN_SETTING_KEYS = [
  'custom_capabilities_configured',
  'custom_photo_enabled',
  'custom_video_enabled',
  'custom_audio_enabled',
  'personalized_audio_fantasy_enabled',
  'custom_name_personalization_enabled',
  'custom_face_allowed',
  'custom_external_items_allowed',
  'custom_outfit_purchase_allowed',
  'custom_prop_purchase_allowed',
  'custom_toy_use_allowed',
  'custom_toy_insertion_allowed',
  'custom_other_object_insertion_allowed',
  'custom_gg_content_allowed',
  'custom_bg_content_allowed',
]

export const CUSTOM_CAPABILITY_LIST_SETTING_KEYS = [
  'custom_allowed_scenarios',
  'custom_forbidden_scenarios',
  'custom_available_outfits',
  'custom_available_locations',
  'custom_available_props',
]

export const CUSTOM_CAPABILITY_CHECKLIST_SETTING_KEYS = [
  'custom_available_prop_categories',
]

const DEFAULTS = {
  custom_requests_enabled: true,
  custom_capabilities_configured: false,
  custom_photo_enabled: false,
  custom_video_enabled: false,
  custom_audio_enabled: false,
  personalized_audio_fantasy_enabled: false,
  personalized_audio_fantasy_min_words: 18,
  personalized_audio_fantasy_soft_offer_after_signals: 2,
  custom_name_personalization_enabled: false,
  custom_face_allowed: false,
  custom_external_items_allowed: false,
  custom_outfit_purchase_allowed: false,
  custom_prop_purchase_allowed: false,
  custom_max_purchase_budget: 0,
  custom_max_nudity_level: 'clothed',
  custom_toy_use_allowed: false,
  custom_toy_insertion_allowed: false,
  custom_other_object_insertion_allowed: false,
  custom_gg_content_allowed: false,
  custom_bg_content_allowed: false,
  custom_max_photo_count: 10,
  custom_max_video_minutes: 10,
  custom_max_audio_minutes: 10,
  custom_standard_turnaround_days: 7,
  custom_allowed_scenarios: [],
  custom_forbidden_scenarios: [],
  custom_available_outfits: [],
  custom_available_locations: [],
  custom_available_prop_categories: [],
  custom_available_props: [],
  custom_pricing_enabled: false,
  custom_pricing_currency: 'USD',
  custom_pricing_base_fee: 30,
  custom_pricing_minimum_total: 75,
  custom_pricing_video_per_minute: 20,
  custom_pricing_audio_per_minute: 8,
  custom_pricing_photo_each: 10,
  custom_pricing_name_markup_percent: 20,
  custom_pricing_urgency_72h_percent: 20,
  custom_pricing_urgency_24h_percent: 40,
  custom_pricing_negotiation_margin_percent: 15,
  custom_pricing_minimum_deposit_percent: 50,
}

export function withCustomPricingDefaults(settings = {}) {
  return { ...DEFAULTS, ...(settings || {}) }
}

function translate(t, key, fallback) {
  if (typeof t !== 'function') return fallback
  const result = t(key)
  return result && result !== key ? result : fallback
}

function pricingSettings(settingsDraft) {
  return Object.fromEntries(
    CUSTOM_PRICING_SETTING_KEYS
      .filter((key) => key in (settingsDraft || {}))
      .map((key) => [key, settingsDraft[key]]),
  )
}

export default function CustomPricingPreview({ configId = null, settingsDraft = {}, t = null }) {
  const [draft, setDraft] = useState({
    content_type: 'video',
    duration_minutes: 5,
    photo_count: 3,
    include_name: true,
    requires_external_items: false,
    external_item_type: 'none',
    actual_expenses: 0,
    urgency_hours: '',
  })
  const [quote, setQuote] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const capabilitiesConfigured = Boolean(settingsDraft.custom_capabilities_configured)
  const availableTypes = useMemo(() => {
    if (!capabilitiesConfigured) return ['video', 'photo', 'audio']
    return ['video', 'photo', 'audio'].filter((kind) => Boolean(settingsDraft[`custom_${kind}_enabled`]))
  }, [
    capabilitiesConfigured,
    settingsDraft.custom_video_enabled,
    settingsDraft.custom_photo_enabled,
    settingsDraft.custom_audio_enabled,
  ])

  useEffect(() => {
    setDraft((prev) => {
      const nextType = availableTypes.includes(prev.content_type) ? prev.content_type : (availableTypes[0] || prev.content_type)
      const nextIncludeName = capabilitiesConfigured && !settingsDraft.custom_name_personalization_enabled ? false : prev.include_name
      const purchasesAllowed = Boolean(settingsDraft.custom_external_items_allowed || settingsDraft.custom_outfit_purchase_allowed || settingsDraft.custom_prop_purchase_allowed)
      const nextExternalItems = capabilitiesConfigured && !purchasesAllowed ? false : prev.requires_external_items
      if (nextType === prev.content_type && nextIncludeName === prev.include_name && nextExternalItems === prev.requires_external_items) return prev
      return { ...prev, content_type: nextType, include_name: nextIncludeName, requires_external_items: nextExternalItems }
    })
    setQuote(null)
  }, [
    capabilitiesConfigured,
    availableTypes,
    settingsDraft.custom_name_personalization_enabled,
    settingsDraft.custom_external_items_allowed,
    settingsDraft.custom_outfit_purchase_allowed,
    settingsDraft.custom_prop_purchase_allowed,
  ])

  function update(key, value) {
    setDraft((prev) => ({ ...prev, [key]: value }))
    setQuote(null)
  }

  async function calculate() {
    setLoading(true)
    setError('')
    try {
      const result = await previewCustomQuote({
        ...draft,
        duration_minutes: Number(draft.duration_minutes) || 0,
        photo_count: Number(draft.photo_count) || 0,
        actual_expenses: Number(draft.actual_expenses) || 0,
        urgency_hours: draft.urgency_hours === '' ? null : Number(draft.urgency_hours),
        settings: pricingSettings(settingsDraft),
      }, configId)
      setQuote(result?.quote || null)
    } catch (err) {
      setError(getApiErrorMessage(err, translate(t, 'agencySettings.customPricing.previewError', 'Failed to calculate quote')))
    } finally {
      setLoading(false)
    }
  }

  const currency = quote?.currency || settingsDraft.custom_pricing_currency || 'USD'
  return (
    <section className="custom-pricing-preview" aria-labelledby="custom-pricing-preview-title">
      <div className="custom-pricing-preview__head">
        <div>
          <h4 id="custom-pricing-preview-title">{translate(t, 'agencySettings.customPricing.previewTitle', 'Quote preview')}</h4>
          <p>{translate(t, 'agencySettings.customPricing.previewHint', 'The floor is protected; the bot opens at the upper price and may negotiate down to the floor.')}</p>
        </div>
      </div>
      <div>
        <div className="custom-pricing-preview__inputs">
          <label>
            <span>{translate(t, 'agencySettings.customPricing.contentType', 'Content type')}</span>
            <select value={draft.content_type} onChange={(event) => update('content_type', event.target.value)}>
              <option value="video" disabled={!availableTypes.includes('video')}>Video</option>
              <option value="photo" disabled={!availableTypes.includes('photo')}>Photo</option>
              <option value="audio" disabled={!availableTypes.includes('audio')}>Audio</option>
            </select>
          </label>
          {draft.content_type === 'photo' ? (
            <label>
              <span>{translate(t, 'agencySettings.customPricing.photoCount', 'Photo count')}</span>
              <input type="number" min="1" max={capabilitiesConfigured ? settingsDraft.custom_max_photo_count : 1000} value={draft.photo_count} onChange={(event) => update('photo_count', event.target.value)} />
            </label>
          ) : (
            <label>
              <span>{translate(t, 'agencySettings.customPricing.duration', 'Duration, minutes')}</span>
              <input type="number" min="1" max={capabilitiesConfigured ? settingsDraft[`custom_max_${draft.content_type}_minutes`] : 1000} step="0.5" value={draft.duration_minutes} onChange={(event) => update('duration_minutes', event.target.value)} />
            </label>
          )}
          <label>
            <span>{translate(t, 'agencySettings.customPricing.urgency', 'Delivery window')}</span>
            <select value={draft.urgency_hours} onChange={(event) => update('urgency_hours', event.target.value)}>
              <option value="">Standard</option>
              <option value="72">Within 72 hours</option>
              <option value="24">Within 24 hours</option>
            </select>
          </label>
          <label>
            <span>{translate(t, 'agencySettings.customPricing.actualExpenses', 'Confirmed item expenses')}</span>
            <input type="number" min="0" max="100000" step="0.01" value={draft.actual_expenses} onChange={(event) => update('actual_expenses', event.target.value)} />
          </label>
        </div>
        <div className="custom-pricing-preview__checks">
          <label><input type="checkbox" checked={draft.include_name} disabled={capabilitiesConfigured && !settingsDraft.custom_name_personalization_enabled} onChange={(event) => update('include_name', event.target.checked)} /> <span>{translate(t, 'agencySettings.customPricing.includeName', 'Name personalization')}</span></label>
          <label><input type="checkbox" checked={draft.requires_external_items} disabled={capabilitiesConfigured && !(settingsDraft.custom_external_items_allowed || settingsDraft.custom_outfit_purchase_allowed || settingsDraft.custom_prop_purchase_allowed)} onChange={(event) => update('requires_external_items', event.target.checked)} /> <span>{translate(t, 'agencySettings.customPricing.externalItems', 'Requires a purchased item')}</span></label>
        </div>
        {draft.requires_external_items ? (
          <label className="custom-pricing-preview__item-type">
            <span>{translate(t, 'agencySettings.customPricing.externalItemType', 'Purchased item type')}</span>
            <select value={draft.external_item_type} onChange={(event) => update('external_item_type', event.target.value)}>
              <option value="none">Other / unknown</option>
              <option value="outfit" disabled={capabilitiesConfigured && !settingsDraft.custom_outfit_purchase_allowed}>Outfit</option>
              <option value="prop" disabled={capabilitiesConfigured && !settingsDraft.custom_prop_purchase_allowed}>Prop</option>
              <option value="other" disabled={capabilitiesConfigured && !settingsDraft.custom_external_items_allowed}>Other item</option>
            </select>
          </label>
        ) : null}
        {capabilitiesConfigured && !availableTypes.length ? <div className="form-error">Enable at least one custom format.</div> : null}
        <button type="button" className="btn btn-secondary" disabled={loading || !availableTypes.length} onClick={calculate}>{loading ? translate(t, 'common.loading', 'Calculating...') : translate(t, 'agencySettings.customPricing.calculate', 'Calculate')}</button>
      </div>
      {error ? <div className="form-error">{error}</div> : null}
      {quote ? (
        <div className="custom-pricing-preview__result">
          <div><span>{translate(t, 'agencySettings.customPricing.floor', 'Minimum sale price')}</span><strong>{quote.minimum_price.toFixed(2)} {currency}</strong></div>
          <div><span>{translate(t, 'agencySettings.customPricing.opening', 'Opening price')}</span><strong>{quote.maximum_price} {currency}</strong></div>
          <div><span>{translate(t, 'agencySettings.customPricing.deposit', 'Deposit at opening price')}</span><strong>{quote.minimum_deposit_at_maximum} {currency} (≥{quote.minimum_deposit_percent}%)</strong></div>
          {quote.external_cost_pending ? <p>{translate(t, 'agencySettings.customPricing.externalPending', 'Final price remains pending until the actual prop or outfit cost is confirmed.')}</p> : null}
        </div>
      ) : null}
    </section>
  )
}
